import { useCallback, useEffect, useReducer, useRef } from 'react';
import { DraftSaver, unsavedDrafts, type DraftOptions, type DraftSave, type DraftStatus } from './draftSaver';

/**
 * Lokaler Entwurf für Textfelder mit verzögertem Speichern (Autosave) und sofortigem Speichern beim Verlassen (siehe DraftSaver):
 * gespeichert ist erst, was der Server bestätigt; Fehlgeschlagenes wird erneut gesendet und nicht vom Stand des Servers überschrieben.
 * `onSave` darf ein Promise liefern – endet es mit `null` (so melden die Aktionen der Geschichte einen Fehler) oder scheitert es,
 * bleibt der Entwurf ungespeichert.
 */
export function useDraft(value: string, onSave: DraftSave, delayMs = 900, opts: Omit<DraftOptions, 'delayMs'> = {}) {
  const ref = useRef<DraftSaver | null>(null);
  if (!ref.current) ref.current = new DraftSaver(value, onSave, { ...opts, delayMs });
  const saver = ref.current;
  saver.setSave(onSave);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    saver.subscribe(rerender);
    return () => saver.dispose();
  }, [saver]);

  useEffect(() => {
    saver.setValue(value);
  }, [saver, value]);

  const change = useCallback((v: string) => saver.change(v), [saver]);
  const flush = useCallback(() => saver.flush(), [saver]);
  const keepMine = useCallback(() => saver.keepMine(), [saver]);
  const discard = useCallback(() => saver.discard(), [saver]);

  return {
    draft: saver.draft,
    change,
    flush,
    dirty: () => saver.unsaved,
    status: saver.status as DraftStatus,
    conflict: saver.conflict,
    restored: saver.restored,
    keepMine,
    discard,
  };
}

/**
 * Seite verlassen: Ungespeichertes sofort senden, wenn die Seite verborgen wird (Tab-Wechsel, Schließen auf Mobilgeräten), und beim
 * Schließen bzw. Neuladen warnen, solange noch etwas unterwegs ist. Einmal in der App einbinden.
 */
export function installDraftGuards(): () => void {
  const onHide = (): void => {
    if (document.visibilityState === 'hidden') for (const d of unsavedDrafts()) d.flush();
  };
  const onBeforeUnload = (e: BeforeUnloadEvent): void => {
    const open = unsavedDrafts();
    if (open.length === 0) return;
    for (const d of open) d.flush();
    e.preventDefault();
    e.returnValue = '';
  };
  // wieder online: Fehlgeschlagenes gleich erneut senden
  const onOnline = (): void => {
    for (const d of unsavedDrafts()) d.flush();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('beforeunload', onBeforeUnload);
  window.addEventListener('online', onOnline);
  return () => {
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('beforeunload', onBeforeUnload);
    window.removeEventListener('online', onOnline);
  };
}
